import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Paper, 
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import api from '../api';
import CompanyResearch from './CompanyResearch';
import logger from './../utils/logger.js';

const CompanySearch = ({ onClose, initialCompanyUrl = '' }) => {
  const [companyUrl, setCompanyUrl] = useState(initialCompanyUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  const [urlRecords, setUrlRecords] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingUrls, setPendingUrls] = useState([]);
  const [foundRecords, setFoundRecords] = useState([]);
  // New state for record creation form
  const [createRecordDialogOpen, setCreateRecordDialogOpen] = useState(false);
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [newRecordData, setNewRecordData] = useState({
    Instellingsnaam: '',
    "Vestigingsadres Plaats": '',
    URL: '',
    Telefoonnummer: '',
    Website: '',
    Domain: ''
  });
  const [createdRecords, setCreatedRecords] = useState([]);
  
  // Function to handle search
  const handleSearch = async () => {
    // Basic validation - just make sure there's some input
    if (!companyUrl || companyUrl.trim().length === 0) {
      setError('Please enter one or more website URLs or domains');
      return;
    }

    // Clear previous error
    setError(null);
    
    // Set loading state
    setLoading(true);

    try {
      // Split input by commas or newlines and clean up
      const urls = companyUrl
        .split(/[\n,]/)
        .map(url => url.trim())
        .filter(url => url.length > 0);

      if (urls.length === 0) {
        setError('Please enter at least one valid website URL or domain');
        setLoading(false);
        return;
      }

      // Reset created records
      setCreatedRecords([]);

      // Process each URL and create records
      const records = [];
      const notFoundUrls = [];
      
      for (const url of urls) {
        // Clean and process the URL to make it suitable for DNS checks
        const cleanUrl = formatUrl(url);
        const domain = extractDomainFromUrl(cleanUrl);
        
        // Simple domain validation
        if (!domain || domain.length === 0 || !domain.includes('.')) {
          setError(`Invalid domain format: ${url}. Please enter a valid website URL or domain (e.g., example.com)`);
          setLoading(false);
          return;
        }
        
        // Check if the URL exists in our records
        logger.log(`Checking if URL exists in records: ${cleanUrl} (Domain: ${domain})`);
        const urlCheckResult = await api.checkUrlInRecords(cleanUrl);
        
        // If the URL exists in records, use those records
        if (urlCheckResult.success && urlCheckResult.found) {
          logger.log(`Found ${urlCheckResult.count} existing records for URL: ${cleanUrl}`);
          
          // Add existing records with basic formatting if needed
          for (const existingRecord of urlCheckResult.records) {
            // Ensure record has a record_id
            const recordWithId = {
              ...existingRecord,
              record_id: existingRecord.record_id || `existing-${Date.now()}-${domain}`
            };
            
            // Add to records array
            records.push(recordWithId);
          }
        } else {
          // If not found, collect the URL for creation confirmation
          logger.log(`No existing records found for URL: ${cleanUrl}, will ask for confirmation`);
          notFoundUrls.push({
            url: cleanUrl,
            domain: domain,
            companyName: extractCompanyNameFromUrl(cleanUrl)
          });
        }
      }

      logger.log(`Found ${records.length} existing records and ${notFoundUrls.length} URLs not found`);
      
      // Store the existing records
      setFoundRecords(records);

      // Check if we have any URLs that were not found
      if (notFoundUrls.length > 0) {
        logger.log("Showing confirmation dialog for URLs not found");
        setPendingUrls(notFoundUrls);
        setConfirmDialogOpen(true);
      } else {
        logger.log("All URLs found, opening research dialog directly");
        // If all URLs were found, proceed directly to research
        setUrlRecords(records);
        setResearchDialogOpen(true);
      }
    } catch (err) {
      logger.error('Error during URL search:', err);
      setError(`Error searching: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if initialCompanyUrl is provided
  useEffect(() => {
    if (initialCompanyUrl && initialCompanyUrl.trim() !== '') {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        handleSearch();
      }, 300);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyUrl]);

  // Function to start the record creation process
  const handleCreateRecords = () => {
    // Close the confirmation dialog
    setConfirmDialogOpen(false);
    
    // If we have URLs to create, show the creation form for the first one
    if (pendingUrls.length > 0) {
      // Set up the initial record data for the first URL
      const firstUrl = pendingUrls[0];
      setNewRecordData({
        Instellingsnaam: firstUrl.companyName,
        "Vestigingsadres Plaats": '',
        URL: firstUrl.url,
        Telefoonnummer: '',
        Website: firstUrl.url,
        Domain: firstUrl.domain
      });
      
      setCurrentUrlIndex(0);
      setCreateRecordDialogOpen(true);
    } else {
      // If no URLs to create, just continue with existing records
      handleRecordCreationComplete();
    }
  };

  // Function to handle input changes in the form
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setNewRecordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to save the current record and move to the next one
  const handleSaveRecord = async () => {
    setLoading(true);
    
    try {
      // Create a record via API
      const result = await api.addRecord(newRecordData);
      
      if (result.success) {
        logger.log(`Successfully created record with ID: ${result.record_id}`);
        
        // Add to created records
        setCreatedRecords(prev => [
          ...prev, 
          { ...result.record, record_id: result.record_id }
        ]);
        
        // Check if there are more URLs to process
        if (currentUrlIndex < pendingUrls.length - 1) {
          // Move to the next URL
          const nextIndex = currentUrlIndex + 1;
          const nextUrl = pendingUrls[nextIndex];
          
          // Set up data for the next URL
          setNewRecordData({
            Instellingsnaam: nextUrl.companyName,
            "Vestigingsadres Plaats": '',
            URL: nextUrl.url,
            Telefoonnummer: '',
            Website: nextUrl.url,
            Domain: nextUrl.domain
          });
          
          setCurrentUrlIndex(nextIndex);
        } else {
          // All records created, close dialog and continue
          setCreateRecordDialogOpen(false);
          handleRecordCreationComplete();
        }
      } else {
        logger.error(`Failed to create record:`, result.message);
        setError(`Failed to create record: ${result.message}`);
      }
    } catch (err) {
      logger.error(`Error creating record:`, err);
      setError(`Error creating record: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to skip the current record and move to the next one
  const handleSkipCurrentRecord = () => {
    // Check if there are more URLs to process
    if (currentUrlIndex < pendingUrls.length - 1) {
      // Move to the next URL
      const nextIndex = currentUrlIndex + 1;
      const nextUrl = pendingUrls[nextIndex];
      
      // Set up data for the next URL
      setNewRecordData({
        Instellingsnaam: nextUrl.companyName,
        "Vestigingsadres Plaats": '',
        URL: nextUrl.url,
        Telefoonnummer: '',
        Website: nextUrl.url,
        Domain: nextUrl.domain
      });
      
      setCurrentUrlIndex(nextIndex);
    } else {
      // All records processed, close dialog and continue
      setCreateRecordDialogOpen(false);
      handleRecordCreationComplete();
    }
  };

  // Function called when record creation is complete
  const handleRecordCreationComplete = () => {
    // Combine created records with any existing records
    const allRecords = [...foundRecords, ...createdRecords];
    
    if (allRecords.length > 0) {
      // Open the research dialog with all records
      setUrlRecords(allRecords);
      setResearchDialogOpen(true);
    } else {
      // Create temporary records for any skipped URLs
      const tempRecords = pendingUrls.map(({ url, domain, companyName }) => ({
        record_id: `url-${Date.now()}-${domain}`,
        Instellingsnaam: companyName,
        company_website: url,
        Website: url,
        website: url,
        URL: url,
        url: url,
        Domain: domain,
        domain: domain
      }));
      
      setUrlRecords([...foundRecords, ...tempRecords]);
      setResearchDialogOpen(true);
    }
  };

  // Function to skip creating records for URLs that were not found
  const handleSkipCreation = async () => {
    logger.log("Skip button clicked");
    
    // Close the confirmation dialog
    setConfirmDialogOpen(false);
    
    // Set loading state
    setLoading(true);
    
    try {
      // Create actual database records for URLs not found (with minimal info)
      const newRecords = [];
      
      for (const { url, domain, companyName } of pendingUrls) {
        logger.log(`Creating minimal database record for ${url}`);
        
        // Create a minimal record object with required fields
        const recordData = {
          Instellingsnaam: companyName,
          "Vestigingsadres Plaats": "-",  // Required field with minimal placeholder
          URL: url,
          Telefoonnummer: "-",  // Required field with minimal placeholder
          Website: url,
          Domain: domain
        };
        
        try {
          // Add the record to the database
          const result = await api.addRecord(recordData);
          
          if (result.success) {
            logger.log(`Successfully created minimal record with ID: ${result.record_id}`);
            newRecords.push({
              ...result.record,
              record_id: result.record_id
            });
          } else {
            logger.error(`Failed to create minimal record for ${url}:`, result.message);
            // Create a temporary record as fallback
            newRecords.push({
              record_id: `url-${Date.now()}-${domain}`,
              Instellingsnaam: companyName,
              company_website: url,
              Website: url,
              URL: url,
              Domain: domain,
              domain: domain,
              _isTemporary: true // Add flag to indicate this is a temporary record
            });
          }
        } catch (err) {
          logger.error(`Error creating minimal record for ${url}:`, err);
          // Create a temporary record as fallback
          newRecords.push({
            record_id: `url-${Date.now()}-${domain}`,
            Instellingsnaam: companyName,
            company_website: url,
            Website: url,
            URL: url,
            Domain: domain,
            domain: domain,
            _isTemporary: true // Add flag to indicate this is a temporary record
          });
        }
      }
      
      // Combine with any existing records
      const allRecords = [...foundRecords, ...newRecords];
      
      // Open the research dialog with all records
      setUrlRecords(allRecords);
      setResearchDialogOpen(true);
    } catch (err) {
      logger.error('Error in skip creation:', err);
      setError(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Format URL to ensure it has protocol (for proper parsing)
  const formatUrl = (url) => {
    if (!url) return '';
    
    // Trim and remove any whitespace
    let cleanUrl = url.trim();
    
    // If it's already a properly formatted URL, return as is
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      return cleanUrl;
    }
    
    // Handle common user inputs like "www.example.com" or just "example.com"
    return `https://${cleanUrl}`;
  };

  // Extract domain from URL to use as company name
  const extractCompanyNameFromUrl = (url) => {
    try {
      // Handle both URL objects and string domains
      let domain;
      
      if (url.startsWith('http://') || url.startsWith('https://')) {
        domain = new URL(url).hostname;
      } else {
        domain = url.split('/')[0]; // Just take the domain part
      }
      
      // Remove www. and .com/.org/etc
      return domain.replace(/^www\./, '').split('.')[0];
    } catch (e) {
      // If URL can't be parsed, try to extract name directly
      const parts = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('.');
      return parts[0] || "Company";
    }
  };
  
  // Extract clean domain from URL
  const extractDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      // Get hostname and remove www prefix if present
      return urlObj.hostname.replace(/^www\./, '');
    } catch (e) {
      // If URL parsing fails, try to extract domain directly
      if (typeof url === 'string') {
        // Remove protocol and www if present
        let domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
        // Remove path and query string
        domain = domain.split('/')[0];
        return domain;
      }
      return url;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCloseResearchDialog = () => {
    setResearchDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Company Research
          <Tooltip title="Research multiple companies by entering their website URLs or domains">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        {onClose && (
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Company Websites"
            variant="outlined"
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter one or more website URLs or domains (e.g., example.com, another.com)"
            InputProps={{
              startAdornment: <LinkIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
            sx={{ alignSelf: 'flex-end' }}
          >
            Research
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Enter one or more company website URLs or domain names to research multiple companies at once. 
          Separate them by commas or newlines. You can enter them with or without "https://" or "www." (e.g., "example.com, another.com").
        </Typography>
      </Paper>

      {/* Confirmation Dialog for creating new records */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleSkipCreation}
      >
        <DialogTitle>Create New Company Records?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingUrls.length === 1 
              ? `The URL "${pendingUrls[0]?.url}" was not found in our database.` 
              : `${pendingUrls.length} URLs were not found in our database.`}
            {foundRecords.length > 0 && ` However, we found ${foundRecords.length} existing records for other URLs you entered.`}
            Would you like to create new company records for these URLs?
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            {pendingUrls.map((item, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                • {item.url} ({item.companyName})
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleSkipCreation} 
            color="primary"
            disabled={loading}
          >
            {foundRecords.length > 0 ? "Skip & Continue with Existing" : "Skip"}
          </Button>
          <Button 
            onClick={handleCreateRecords} 
            color="primary" 
            variant="contained"
            startIcon={<AddIcon />}
            disabled={loading}
          >
            Create Records
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Dialog for creating a new record */}
      <Dialog
        open={createRecordDialogOpen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Create Company Record ({currentUrlIndex + 1} of {pendingUrls.length})
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleSkipCurrentRecord} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="Instellingsnaam"
                  value={newRecordData.Instellingsnaam}
                  onChange={handleFormChange}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="City"
                  name="Vestigingsadres Plaats"
                  value={newRecordData["Vestigingsadres Plaats"]}
                  onChange={handleFormChange}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="Telefoonnummer"
                  value={newRecordData.Telefoonnummer}
                  onChange={handleFormChange}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website URL"
                  name="URL"
                  value={newRecordData.URL}
                  onChange={handleFormChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <LinkIcon color="action" sx={{ mr: 1 }} />,
                  }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleSkipCurrentRecord} 
            color="primary"
            disabled={loading}
          >
            Skip This URL
          </Button>
          <Button 
            onClick={handleSaveRecord} 
            color="primary" 
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            disabled={loading}
          >
            Save Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* CompanyResearch Dialog for URL-based research */}
      <Dialog 
        open={researchDialogOpen}
        onClose={handleCloseResearchDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Research for {urlRecords.length} Companies
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseResearchDialog} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {urlRecords.length > 0 && (
            <CompanyResearch 
              selectedRecords={urlRecords} 
              onClose={handleCloseResearchDialog} 
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompanySearch; 